import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';

import { Icono } from '#components/mobile/ui/Icono';
import { color, movimiento } from '#components/mobile/ui/tokens';
import { TOP_AVISOS_MOVIL } from '#components/Notifications';

import { ULTIMO_PASO } from './estado';
import { ALTO_CABECERA, Z_TRAVESIA } from './pasos/comun';

/** La app se baja lo que mide la cabecera mientras está a la vista. */
const ESTILO_DESPLAZA =
  `body[data-travesia-cabecera] [data-travesia-desplaza]{padding-top:${ALTO_CABECERA}px}` +
  // Los avisos (arriba en el móvil) salen bajo la cabecera, no tapados por ella.
  `body[data-travesia-cabecera] [data-avisos]{top:calc(${TOP_AVISOS_MOVIL} + ${ALTO_CABECERA}px)!important}`;

type CabeceraPasoProps = {
  /** Paso en curso (1..9). */
  paso: number;
  /** Sin «Saltar» (paso final) o mientras se escribe. */
  onSaltar?: () => void;
  saltarDesactivado?: boolean;
  /** Vuelve al paso anterior; sin él (paso 2) no sale la flecha. */
  onAtras?: () => void;
};

/**
 * Barra fija de la travesía: «Atrás», progreso, «Paso N de 9» y «Saltar» (como
 * `.tuto` de la maqueta). Baja el contenido de la app 40 px (atributo
 * `data-travesia-desplaza` en la vista con scroll de `FinancesApp`).
 */
export function CabeceraPaso({
  paso,
  onSaltar,
  saltarDesactivado,
  onAtras,
}: CabeceraPasoProps) {
  const { t } = useTranslation();

  useEffect(() => {
    document.body.dataset.travesiaCabecera = '';
    return () => {
      delete document.body.dataset.travesiaCabecera;
    };
  }, []);

  return createPortal(
    <div
      data-testid="cabecera-paso"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: Z_TRAVESIA.cabecera,
        boxSizing: 'content-box',
        height: ALTO_CABECERA,
        paddingTop: 'env(safe-area-inset-top, 0px)',
        backgroundColor: color.bg,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        paddingLeft: 16,
        paddingRight: 16,
      }}
    >
      <style>{ESTILO_DESPLAZA}</style>
      {onAtras && (
        <Button
          variant="bare"
          data-testid="travesia-atras"
          aria-label={t('Back')}
          isDisabled={saltarDesactivado}
          onPress={onAtras}
          style={{
            width: 36,
            height: 36,
            marginLeft: -8,
            borderRadius: 18,
            color: color.fg,
            flexShrink: 0,
            opacity: saltarDesactivado ? 0.5 : 1,
          }}
        >
          <Icono nombre="cl" size={20} />
        </Button>
      )}
      <span
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={ULTIMO_PASO}
        aria-valuenow={paso}
        aria-label={t('Step {{n}} of {{total}}', {
          n: paso,
          total: ULTIMO_PASO,
        })}
        style={{
          flex: 1,
          height: 4,
          borderRadius: 99,
          overflow: 'hidden',
          backgroundColor: color.surface3,
        }}
      >
        <i
          style={{
            display: 'block',
            height: '100%',
            width: `${Math.round((paso / ULTIMO_PASO) * 100)}%`,
            backgroundColor: color.accent,
            borderRadius: 99,
            transition: `width .4s ${movimiento.muelle}`,
          }}
        />
      </span>
      <span
        data-testid="cabecera-paso-cuenta"
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: color.fg3,
          whiteSpace: 'nowrap',
        }}
      >
        {t('Step {{n}} of {{total}}', { n: paso, total: ULTIMO_PASO })}
      </span>
      {onSaltar && (
        <Button
          variant="bare"
          data-testid="travesia-saltar"
          isDisabled={saltarDesactivado}
          onPress={onSaltar}
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: color.accent,
            minHeight: 44,
            padding: '0 2px',
            whiteSpace: 'nowrap',
            opacity: saltarDesactivado ? 0.5 : 1,
          }}
        >
          <Trans>Skip</Trans>
        </Button>
      )}
    </div>,
    document.body,
  );
}
